import { useLayoutEffect, useRef } from "react";
import { Trash2 } from "lucide-react";
import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog";

/**
 * Issue #762 — the in-app delete-confirm dialog, replacing the native
 * `confirm("Delete match?")` the shipped list uses.
 *
 * Issue #1124 (D221 O8, D223 Q1) — this is the one in-app delete-match
 * confirmation, everywhere a player can delete a match: the kit's red alert
 * dialog, the delete verb in `--nw-alert` with a navy Keep it (D55).
 * #762's navy ("in navy with no red", brief-762.md ruling 8) was superseded
 * by round 6b's `MatchListFold.dc.html` drawing of this same dialog; there is
 * no tone prop any more.
 *
 * Built on the kit's Radix `ui/alert-dialog` (#1124 review F1), so it is a real
 * modal and not just a role: focus moves in (to Keep it, the safe choice), Tab
 * stays inside, Escape is Keep it, and focus returns to the control that
 * opened it. "Delete match" is a plain button, not `AlertDialogAction`, which
 * would close the dialog on press: a failed delete stays on the dialog (D57).
 * Radix does not dismiss an alert dialog on an outside click, on purpose.
 * The scrim is the shipped rgba(15,23,42,0.4) (#762) and the only motion is a
 * fade (none under prefers-reduced-motion), so the primitives are composed here
 * and not taken from the shared wrapper, whose scrim and zoom/slide are not drawn.
 * While a delete is in flight, closing is ignored and Keep it is disabled, so a
 * failure still has its dialog to appear on (D57).
 * It gives focus back to the element that was focused when it opened; if that
 * element is gone (the card was just deleted), to the screen's title, which
 * carries `data-focus-after-delete`.
 *
 * Moved out of `SeasonRecordScreen.jsx` into its own file for issue #900
 * (`OneInnings.dc.html`'s own drawing has no delete affordance at all — see
 * `MatchRow.jsx`'s own header): `RecordOneMatchScreen.jsx` needs the exact
 * same confirm dialog for its one match, and a second, drifting copy of
 * this markup is exactly the kind of duplication this codebase's own
 * conventions (`recordFigures.js`, `counters.js`) refuse elsewhere. Same
 * testids (`perf-delete-dialog`, `perf-delete-cancel`, `perf-delete-confirm`),
 * same markup — moved, not rewritten.
 *
 * @param {object} props
 * @param {{opponent?: string, match_date?: string}} props.target — the
 *   match pending delete; the dialog is not rendered at all while this is
 *   falsy (the caller owns that condition, same as before extraction).
 * @param {boolean} [props.deleting]
 * @param {string|null} [props.error] — D57: the failure stays on the
 *   dialog, named plainly, not a toast.
 * @param {() => void} props.onCancel
 * @param {() => void} props.onConfirm
 */
export function DeleteMatchDialog({ target, deleting = false, error = null, onCancel, onConfirm }) {
  // There is no Radix trigger (the callers open this from their own state), so
  // Radix has nothing to give focus back to: remember the opener, return to it.
  const opener = useRef(null);
  const isOpen = Boolean(target);
  useLayoutEffect(() => {
    if (isOpen) opener.current = document.activeElement;
  }, [isOpen]);
  return (
    <AlertDialogPrimitive.Root open={isOpen} onOpenChange={(open) => { if (!open && !deleting) onCancel(); }}>
      {target && (
        <AlertDialogPrimitive.Portal>
          <AlertDialogPrimitive.Overlay
            className="nw-alert-fade"
            style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.4)", zIndex: 50 }}
          />
        <AlertDialogPrimitive.Content
          className="nw-alert-fade"
          data-testid="perf-delete-dialog"
          aria-modal="true"
          onCloseAutoFocus={(e) => {
            e.preventDefault();
            const el = opener.current;
            opener.current = null;
            const back = el && el.isConnected ? el : document.querySelector("[data-focus-after-delete]");
            if (back && typeof back.focus === "function") back.focus();
          }}
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 50,
            display: "flex",
            flexDirection: "column",
            gap: 12,
            padding: 18,
            background: "var(--nw-surface)",
            border: "1px solid var(--nw-line-strong)",
            borderRadius: 14,
            boxShadow: "0 8px 24px rgba(15,23,42,0.12)",
            maxWidth: 360,
            width: "calc(100% - 40px)",
          }}
        >
          <AlertDialogPrimitive.Title style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: 17, lineHeight: "normal", color: "var(--nw-ink)" }}>
            Delete this match?
          </AlertDialogPrimitive.Title>
          <AlertDialogPrimitive.Description className="nw-prose" style={{ color: "inherit" }}>
            {target.opponent} · {target.match_date}. The innings comes off your record. This can&#x27;t be undone.
          </AlertDialogPrimitive.Description>
          {error && <div className="nw-help" style={{ color: "var(--nw-ink)" }}>{error}</div>}
          <div style={{ display: "flex", gap: 10 }}>
            <AlertDialogPrimitive.Cancel
              data-testid="perf-delete-cancel"
              disabled={deleting}
              style={{
                display: "flex",
                flex: 1,
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
                minHeight: 48,
                margin: 0,
                background: "var(--nw-surface)",
                border: "1px solid var(--nw-line-strong)",
                color: "var(--nw-navy)",
                fontWeight: 600,
                fontSize: 14,
                borderRadius: "var(--nw-radius-control)",
                opacity: deleting ? 0.7 : 1,
              }}
            >
              Keep it
            </AlertDialogPrimitive.Cancel>
            <button
              type="button"
              data-testid="perf-delete-confirm"
              onClick={onConfirm}
              disabled={deleting}
              style={{
                display: "flex",
                flex: 1,
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                minHeight: 48,
                background: "var(--nw-alert)",
                color: "#FFFFFF",
                fontWeight: 700,
                fontSize: 15,
                borderRadius: "var(--nw-radius-control)",
                border: "none",
                opacity: deleting ? 0.7 : 1,
                cursor: deleting ? "not-allowed" : "pointer",
              }}
            >
              <Trash2 size={16} aria-hidden="true" />
              Delete match
            </button>
          </div>
        </AlertDialogPrimitive.Content>
        </AlertDialogPrimitive.Portal>
      )}
    </AlertDialogPrimitive.Root>
  );
}
